import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  CreditCard,
  ArrowLeft,
  Search,
  Calendar,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
} from "lucide-react";

import paymentService from "@/services/paymentService";
import authService from "@/services/authService";

const CURRENCIES = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AED: "د.إ",
  INR: "₹",
  CAD: "C$",
  AUD: "A$",
  SGD: "S$",
};

const PURPOSE_LABELS = {
  CROWD_CONTRIBUTION: "Crowdfund",
  INDIVIDUAL_FUNDING: "Individual",
  ENCRYPTION_ADDON: "Encryption",
};

export default function PaymentHistory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [isGuest, setIsGuest] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const [lookupReference, setLookupReference] = useState("");

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    try {
      if (authService.isAuthenticated()) {
        setIsGuest(false);
        await loadHistory();
      } else {
        setIsGuest(true);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error checking auth:", error);
      setIsGuest(true);
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getHistory();
      setTransactions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading history:", error);
      if (error.response?.status === 401) {
        setIsGuest(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLookup = async () => {
    if (!guestEmail) {
      toast.error("Please enter your email");
      return;
    }

    try {
      setLoading(true);
      const data = await paymentService.lookupPayments(guestEmail, lookupReference || null);
      setTransactions(Array.isArray(data) ? data : []);
      
      if (data.length === 0) {
        toast.info("No payments found for this email");
      }
    } catch (error) {
      console.error("Error looking up payments:", error);
      toast.error("Failed to lookup payments");
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    return CURRENCIES[currency] || "$";
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="payment-history">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1
              className="text-2xl font-bold text-slate-900 cursor-pointer"
              style={{ fontFamily: "Playfair Display, serif" }}
              onClick={() => navigate("/dashboard")}
            >
             Golden Records
            </h1>
            <Button
              variant="outline"
              onClick={() => navigate("/participate")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <h1
              className="text-3xl font-bold text-black"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Payment History
            </h1>
            <p className="text-slate-600 mt-2">
              {isGuest ? "Look up your payments" : "View your past transactions"}
            </p>
          </div>

          {/* Guest Lookup Form */}
          {isGuest && (
            <Card className="bg-white border-2 border-slate-200 mb-8">
              <CardHeader>
                <CardTitle className="text-lg">Lookup Your Payments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1"
                    data-testid="lookup-email-input"
                  />
                  <Input
                    value={lookupReference}
                    onChange={(e) => setLookupReference(e.target.value)}
                    placeholder="Reference (optional)"
                    className="w-48"
                    data-testid="lookup-ref-input"
                  />
                  <Button
                    onClick={handleGuestLookup}
                    className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500"
                    data-testid="lookup-btn"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Look Up
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto" />
              <p className="mt-4 text-slate-600">Loading transactions...</p>
            </div>
          )}

          {/* Transactions List */}
          {!loading && transactions.length > 0 && (
            <div className="space-y-4">
              {transactions.map((txn) => (
                <Card
                  key={txn.id}
                  className="bg-white border-2 border-slate-200 hover:border-purple-200 transition-all"
                  data-testid={`transaction-${txn.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            txn.status === "SUCCEEDED"
                              ? "bg-green-100"
                              : "bg-red-100"
                          }`}
                        >
                          {txn.status === "SUCCEEDED" ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-black">
                            {getCurrencySymbol(txn.currency)}
                            {txn.total_amount?.toFixed(2)}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Calendar className="w-3 h-3" />
                            {formatDate(txn.created_at)}
                          </div>
                        </div>
                      </div>

                      <div className="text-right">
                        <Badge
                          className={
                            txn.status === "SUCCEEDED"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }
                        >
                          {txn.status}
                        </Badge>
                        <div className="flex gap-1 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {PURPOSE_LABELS[txn.purpose]}
                          </Badge>
                        </div>
                        {txn.provider_txn_id && (
                          <p className="text-xs text-slate-400 mt-1">
                            {txn.provider_txn_id}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && transactions.length === 0 && !isGuest && (
            <Card className="bg-white border-2 border-slate-200">
              <CardContent className="py-12 text-center">
                <CreditCard className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-black mb-2">
                  No transactions yet
                </h3>
                <p className="text-slate-600 mb-4">
                  Your payment history will appear here
                </p>
                <Button
                  onClick={() => navigate("/participate")}
                  className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500"
                >
                  Make Your First Contribution
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Login Prompt for Guest */}
          {isGuest && transactions.length === 0 && !loading && (
            <Card className="bg-gradient-to-r from-pink-50 via-purple-50 to-blue-50 border-2 border-purple-200">
              <CardContent className="py-8 text-center">
                <p className="text-slate-600 mb-4">
                  Log in to see all your payment history automatically
                </p>
                <Button
                  onClick={() => navigate("/login")}
                  variant="outline"
                >
                  Log In
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
